import React, { useState } from 'react';
import { Plus, GripVertical, Edit2, Trash2 } from 'lucide-react';
import { ComponentRenderer } from './ComponentRenderer';
import { ComponentEditModal } from './ComponentEditModal';
import { InsertComponentLine } from './InsertComponentLine';
import type { Component } from '../schemas/validation';
import { cn } from '../utils/cn';

interface LayoutComponentManagerProps {
  components: Component[];
  onComponentsChange: (components: Component[]) => void;
  onAddComponent?: (position?: number) => void;
  onDeleteComponent?: (component: Component) => void;
  siteId?: string;
}

export const LayoutComponentManager: React.FC<LayoutComponentManagerProps> = ({
  components,
  onComponentsChange,
  onAddComponent,
  onDeleteComponent,
  siteId,
}) => {
  const [editingComponent, setEditingComponent] = useState<Component | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleEditClick = (component: Component) => {
    setEditingComponent(component);
    setIsEditModalOpen(true);
  };

  const handleEditSave = async (_componentId: string, data: any) => {
    if (!editingComponent) return;

    const updatedComponents = components.map(c =>
      c.id === editingComponent.id ? { ...c, data, updatedAt: new Date().toISOString() } : c
    );

    onComponentsChange(updatedComponents);
    setIsEditModalOpen(false);
    setEditingComponent(null);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newComponents = [...components];
      const [draggedItem] = newComponents.splice(draggedIndex, 1);
      newComponents.splice(dragOverIndex, 0, draggedItem);

      // Update order positions
      const reorderedComponents = newComponents.map((c, idx) => ({
        ...c,
        order: idx,
      }));

      onComponentsChange(reorderedComponents);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  // Sort components by order
  const sortedComponents = [...components].sort((a, b) => (a.order || 0) - (b.order || 0));

  if (components.length === 0) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center text-gray-500 p-8">
        <p className="mb-4">No components yet. Add your first component!</p>
        {onAddComponent && (
          <button
            onClick={() => onAddComponent(0)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus size={18} />
            <span>Add Component</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {sortedComponents.map((component, index) => (
        <div key={component.id}>
          {/* Insert line before component */}
          {onAddComponent && (
            <div
              className={cn('transition-all', dragOverIndex === index && 'py-4 bg-blue-50')}
              onDragOver={e => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
            >
              <InsertComponentLine position={index} onInsert={onAddComponent} />
            </div>
          )}

          {/* Component */}
          <div
            className={cn(
              'relative group',
              'border-2 border-transparent hover:border-purple-300 rounded-lg',
              draggedIndex === index && 'opacity-50'
            )}
            draggable
            onDragStart={e => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
          >
            {/* Component controls */}
            <div className="absolute top-2 right-2 z-[60] flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                className="p-1.5 bg-white rounded shadow-sm hover:bg-gray-50 cursor-grab active:cursor-grabbing"
                title="Drag to reorder"
              >
                <GripVertical size={16} className="text-gray-500" />
              </button>
              <button
                onClick={() => handleEditClick(component)}
                className="p-1.5 bg-white rounded shadow-sm hover:bg-blue-50"
                title="Edit component"
              >
                <Edit2 size={16} className="text-blue-600" />
              </button>
              {component.type !== 'content-area' && onDeleteComponent && (
                <button
                  onClick={() => onDeleteComponent(component)}
                  className="p-1.5 bg-white rounded shadow-sm hover:bg-red-50"
                  title="Delete component"
                >
                  <Trash2 size={16} className="text-red-600" />
                </button>
              )}
            </div>

            {/* Preview only — block nav/link clicks; edit via toolbar above */}
            <div className="pointer-events-none" data-layout-editor-preview>
              <ComponentRenderer component={component} isEditing={false} />
            </div>
          </div>
        </div>
      ))}

      {/* Insert line after last component */}
      {onAddComponent && (
        <div
          className={cn(
            'transition-all',
            dragOverIndex === sortedComponents.length && 'py-4 bg-blue-50'
          )}
          onDragOver={e => handleDragOver(e, sortedComponents.length)}
          onDragLeave={handleDragLeave}
        >
          <InsertComponentLine position={sortedComponents.length} onInsert={onAddComponent} />
        </div>
      )}

      {/* Edit Modal */}
      {editingComponent && (
        <ComponentEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingComponent(null);
          }}
          component={editingComponent}
          onSave={handleEditSave}
          siteId={siteId}
        />
      )}
    </div>
  );
};
